import React from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { HStack } from '../hstack'
import { Icon, type IconSource } from '../icon'
import { Text } from '../text'
import { type TextWeight } from '../text/variants'
import { resolveTimelineBar, TimelineBarTone, TimelineBarVisual } from './variants'

export type TimelineBarProps = TimelineBarVisual & {
    name: string
    icon?: IconSource
    /** Secondary text rendered after `name` (assignee on task bars, body text on NOTAM bars). */
    person?: string
    personIcon?: IconSource
    /** Vertical hairline between `name` and `person` (Figma `AOS/NOTAM` bar). */
    divider?: boolean
    rightText?: string
    rightIcon?: IconSource
    isClippedLeft?: boolean
    isClippedRight?: boolean
    clipMarker?: boolean
    nameWeight?: TextWeight
    personWeight?: TextWeight
    rightTextWeight?: TextWeight
    className?: string
    testID?: string
}

const inkClassByTone: Record<TimelineBarTone, string> = {
    'default': 'text-content-default',
    'on-graphics': 'text-content-inverted',
    'knockout': 'text-content-knockout',
}

const dividerClassByTone: Record<TimelineBarTone, string> = {
    'default': 'bg-content-muted',
    'on-graphics': 'bg-content-inverted',
    'knockout': 'bg-content-knockout',
}

const timelineTextMarker = { 'data-timeline-text': '' } as Record<string, unknown>

export const TimelineBar: React.FC<TimelineBarProps> = ({
    background,
    border,
    borderStyle,
    name,
    icon,
    person,
    personIcon,
    divider,
    rightText,
    rightIcon,
    isClippedLeft,
    isClippedRight,
    clipMarker,
    nameWeight = 'regular',
    personWeight = 'regular',
    rightTextWeight = 'regular',
    className,
    testID,
}) => {
    const { container, textTone } = resolveTimelineBar({ background, border, borderStyle })
    const inkClass = inkClassByTone[textTone]
    const markerProps = clipMarker ? timelineTextMarker : undefined

    return (
        <HStack
            testID={testID}
            space='sm'
            className={twMerge(
                'h-10 items-center overflow-hidden rounded px-4 py-2',
                container,
                isClippedLeft && 'rounded-l-none',
                isClippedRight && 'rounded-r-none',
                className,
            )}
        >
            {icon ? <Icon as={icon} size={24} className={inkClass} /> : null}
            <HStack space='sm' className='min-w-0 shrink items-center'>
                <Text
                    size='body'
                    weight={nameWeight}
                    tone={textTone}
                    numberOfLines={1}
                    className='shrink truncate'
                    {...markerProps}
                >
                    {name}
                </Text>
                {person && divider ? (
                    <Box className={twMerge('h-6 w-px shrink-0', dividerClassByTone[textTone])} />
                ) : null}
                {person ? (
                    <HStack space='xs' className='min-w-0 shrink items-center'>
                        {personIcon ? (
                            <Icon as={personIcon} size={16} className={inkClass} />
                        ) : null}
                        <Text
                            size='body'
                            weight={personWeight}
                            tone={textTone}
                            numberOfLines={1}
                            className='shrink truncate'
                            {...markerProps}
                        >
                            {person}
                        </Text>
                    </HStack>
                ) : null}
            </HStack>
            {rightText ? (
                <Text
                    size='body'
                    weight={rightTextWeight}
                    tone={textTone}
                    numberOfLines={1}
                    className='ml-auto min-w-0 shrink pl-2'
                    {...markerProps}
                >
                    {rightText}
                </Text>
            ) : null}
            {rightIcon ? (
                // `ml-auto` is load-bearing - it pushes the glyph to the right edge when there is
                // no rightText.
                <Icon
                    as={rightIcon}
                    size={24}
                    className={twMerge(!rightText && 'ml-auto', inkClass)}
                />
            ) : null}
        </HStack>
    )
}

TimelineBar.displayName = 'TimelineBar'
